import { useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import type { PDFDocumentLoadingTask, PDFDocumentProxy, RenderTask } from "pdfjs-dist";
import { AlertCircle, Loader2, ZoomIn, ZoomOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { createPdfPreviewTask } from "./pdf-preview-loader";
import { isPreviewAssetFailure } from "./pdf-design-recovery";

function PdfPage({ document, number, width, onRetry }: { document: PDFDocumentProxy; number: number; width: number; onRetry: () => void }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  useEffect(() => {
    let disposed = false;
    let task: RenderTask | undefined;
    setStatus("loading");
    const element = canvas.current!;
    // Each render gets its own canvas. A cancelled task must never paint over
    // the next size's canvas while its cancellation promise is settling.
    const buffer = window.document.createElement("canvas");
    void (async () => {
      const page = await document.getPage(number);
      if (disposed) return;
      const natural = page.getViewport({ scale: 1 });
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      const viewport = page.getViewport({ scale: width / natural.width * ratio });
      buffer.width = Math.ceil(viewport.width);
      buffer.height = Math.ceil(viewport.height);
      task = page.render({ canvas: buffer, viewport });
      await task.promise;
      if (disposed) return;
      element.width = buffer.width;
      element.height = buffer.height;
      const context = element.getContext("2d");
      if (!context) throw new Error("Canvas unavailable");
      context.drawImage(buffer, 0, 0);
      setStatus("ready");
    })().catch((error) => {
      if (!disposed) {
        Sentry.captureException(error, { tags: { feature: "pdf_preview", stage: "render" } });
        setStatus("error");
      }
    }).finally(() => {
      buffer.width = 0;
      buffer.height = 0;
    });
    return () => { disposed = true; task?.cancel(); };
  }, [document, number, width]);
  return (
    <figure className="mx-auto shrink-0" style={{ width }} data-testid="pdf-preview-page">
      <figcaption className="mb-2 text-center text-xs text-muted-foreground">Page {number} of {document.numPages}</figcaption>
      <div className="relative min-h-32 bg-white shadow-md" aria-busy={status === "loading"}>
        {status === "loading" && <p role="status" className="absolute inset-0 flex items-center justify-center gap-2 text-sm text-slate-600"><Loader2 className="h-4 w-4 animate-spin" />Rendering page {number}</p>}
        {status === "error" && <div role="alert" className="space-y-3 p-6 text-center text-sm"><p className="text-destructive">Page {number} could not be displayed. Retry or download the PDF.</p><Button size="sm" variant="outline" onClick={onRetry}>Try again</Button></div>}
        <canvas ref={canvas} role="img" aria-label={`PDF page ${number} of ${document.numPages}`} data-ready={status === "ready"} className={status === "ready" ? "block h-auto w-full" : "invisible h-32 w-full"} />
      </div>
    </figure>
  );
}

/** Render the actual downloadable PDF without browser PDF plugins or frames. */
export function PdfDocumentPreview({ blob, onRetry, onAssetError }: { blob: Blob; onRetry: () => void; onAssetError?: (error: unknown) => void }) {
  const [loaded, setLoaded] = useState<{ blob: Blob; document: PDFDocumentProxy } | null>(null);
  const [failed, setFailed] = useState<Blob | null>(null);
  const [assetFailure, setAssetFailure] = useState<Blob | null>(null);
  const [zoom, setZoom] = useState(1);
  const [width, setWidth] = useState(0);
  const host = useRef<HTMLDivElement>(null);
  const assetError = useRef(onAssetError);
  assetError.current = onAssetError;

  useEffect(() => {
    let disposed = false;
    let task: PDFDocumentLoadingTask | undefined;
    setLoaded(null);
    setFailed(null);
    void (async () => {
      const bytes = new Uint8Array(await blob.arrayBuffer());
      if (disposed) return;
      const pending = await createPdfPreviewTask(bytes);
      task = pending;
      if (disposed) { await pending.destroy(); return; }
      const document = await pending.promise;
      if (!disposed) setLoaded({ blob, document });
    })().catch((error) => {
      if (!disposed) {
        if (isPreviewAssetFailure(error)) setAssetFailure(blob);
        assetError.current?.(error);
        Sentry.captureException(error, { tags: { feature: "pdf_preview", stage: "load" } });
        setFailed(blob);
      }
    });
    return () => {
      disposed = true;
      if (task) void task.destroy().catch(() => {});
    };
  }, [blob]);

  useEffect(() => {
    const element = host.current!;
    const measure = () => setWidth(Math.max(0, Math.floor(element.getBoundingClientRect().width)));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const document = loaded?.blob === blob ? loaded.document : null;
  const pageWidth = Math.max(160, Math.floor(Math.min(width, 850) * zoom));
  return (
    <div ref={host} className="min-w-0" data-testid="pdf-document-preview">
      <div className="sticky top-0 z-10 mb-4 flex flex-wrap items-center justify-between gap-2 rounded-md border bg-background/95 p-2 text-xs">
        <span role="status">{document ? `${document.numPages} pages` : failed === blob ? "Preview unavailable" : "Loading PDF…"}</span>
        <div className="flex items-center gap-1">
          <Button size="icon" variant="ghost" className="h-7 w-7" aria-label="Zoom out" disabled={zoom <= 1} onClick={() => setZoom(v => Math.max(1, v - 0.25))}><ZoomOut className="h-4 w-4" /></Button>
          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setZoom(1)} aria-label="Fit PDF to width">{zoom === 1 ? "Fit width" : `${Math.round(zoom * 100)}%`}</Button>
          <Button size="icon" variant="ghost" className="h-7 w-7" aria-label="Zoom in" disabled={zoom >= 2} onClick={() => setZoom(v => Math.min(2, v + 0.25))}><ZoomIn className="h-4 w-4" /></Button>
        </div>
      </div>
      {failed === blob ? (
        <div role="alert" className="flex flex-col items-center gap-3 p-6 text-center text-sm">
          <AlertCircle className="h-6 w-6 text-destructive" />
          <p>{assetFailure === blob
            ? "The app has been updated. Save your design, then reload this page to restore Preview. You can still download the PDF above."
            : "The PDF could not be displayed. Please retry, or download it using the button above."}</p>
          <Button size="sm" variant="outline" onClick={onRetry}>Try again</Button>
        </div>
      ) : document && width > 0 ? (
        <div className="space-y-6" style={{ minWidth: pageWidth }} data-testid="pdf-preview-pages">
          {Array.from({ length: document.numPages }, (_, i) => <PdfPage key={i} document={document} number={i + 1} width={pageWidth} onRetry={onRetry} />)}
        </div>
      ) : <p role="status" className="flex justify-center gap-2 p-10 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Preparing pages</p>}
    </div>
  );
}
